import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, CloudOff, Loader2, XCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import { ApproveRequestDialog } from "./ApproveRequestDialog";
import type { AccessRequestRow, AccessRequestEmailStatus } from "./accessRequestTypes";

const API_URL = process.env.REACT_APP_API_URL || "";
const PAGE_SIZE = 20;

type Filter = "PENDING" | "ALL";

interface Props {
  onChange?: () => void;
}

/**
 * Admin «Заявки» tab (ticket 6): pending access requests with the registry
 * check, approve / reject, and an expandable row with the snapshot the server
 * fetched by isikukood — so nobody has to look the applicant up by hand.
 */
export function AccessRequestsTab({ onChange }: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<AccessRequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>("PENDING");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [approving, setApproving] = useState<AccessRequestRow | null>(null);
  // A failed load must never look like "no requests" (review r2-5)
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const res = await axios.get<{ success: boolean; data: AccessRequestRow[]; pagination: { pages: number } }>(
        `${API_URL}/api/admin/access-requests?status=${filter}&page=${page}&limit=${PAGE_SIZE}`,
        { withCredentials: true }
      );
      if (res.data.success) {
        setItems(res.data.data);
        setPages(res.data.pagination.pages);
      } else {
        setLoadFailed(true);
      }
    } catch {
      setItems([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const errorText = (err: any): string => {
    const code = err?.response?.data?.code as string | undefined;
    return t(`admin.accessRequests.errors.${code && ERROR_CODES.has(code) ? code : "generic"}`);
  };

  const act = async (row: AccessRequestRow, action: "reject" | "recheck" | "resend-email") => {
    if (action === "reject" && !window.confirm(t("admin.accessRequests.confirmReject"))) return;
    setBusy(row.id);
    try {
      const res = await axios.post<{ success: boolean; data: any }>(
        `${API_URL}/api/admin/access-requests/${row.id}/${action}`,
        {},
        { withCredentials: true }
      );
      const outcome = res.data.data?.emailOutcome as AccessRequestEmailStatus | undefined;
      if (outcome) toast.success(t("admin.accessRequests.toastDone", { status: t(`admin.accessRequests.emailStatus.${outcome}`) }));
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(null);
      await fetchRows();
      onChange?.();
    }
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const colCount = 7;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant={filter === "PENDING" ? "default" : "outline"} size="sm" onClick={() => { setPage(1); setFilter("PENDING"); }}>
          {t("admin.accessRequests.filterPending")}
        </Button>
        <Button variant={filter === "ALL" ? "default" : "outline"} size="sm" onClick={() => { setPage(1); setFilter("ALL"); }}>
          {t("admin.accessRequests.filterAll")}
        </Button>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>{t("admin.accessRequests.colDate")}</TableHead>
              <TableHead>{t("admin.accessRequests.colName")}</TableHead>
              <TableHead>{t("admin.accessRequests.colCompany")}</TableHead>
              <TableHead>{t("admin.accessRequests.colCheck")}</TableHead>
              <TableHead>{t("admin.accessRequests.colStatus")}</TableHead>
              <TableHead className="text-right">{t("admin.accessRequests.colActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </TableCell>
              </TableRow>
            )}
            {!loading && loadFailed && (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center py-6 text-destructive">
                  {t("admin.accessRequests.loadFailed")}
                </TableCell>
              </TableRow>
            )}
            {!loading && !loadFailed && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center py-6 text-muted-foreground">
                  {t("admin.accessRequests.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((row) => {
                const open = expanded.has(row.id);
                const canApprove = row.registryOutcome === "VERIFIED" && row.status !== "APPROVED" && busy !== row.id;
                return (
                  <React.Fragment key={row.id}>
                    <TableRow>
                      <TableCell className="align-top">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={open ? t("admin.accessRequests.collapse") : t("admin.accessRequests.expand")}
                          onClick={() => toggle(row.id)}
                        >
                          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div>{new Date(row.createdAt).toLocaleDateString()}</div>
                        <div className="text-xs text-muted-foreground">{new Date(row.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      </TableCell>
                      <TableCell>
                        <div>{[row.givenName, row.familyName].filter(Boolean).join(" ") || "—"}</div>
                        <div className="font-mono text-xs text-muted-foreground">{row.personalCode}</div>
                      </TableCell>
                      <TableCell>
                        <div>{row.companyName}</div>
                        <div className="text-xs text-muted-foreground">
                          <span className="font-mono">{row.registryCode}</span>
                          {" · "}
                          <Badge variant="outline">
                            {row.role === "BUILDING" ? t("admin.accessRequests.roleBuilding") : t("admin.accessRequests.roleProvider")}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell><RegistryCheckBadge row={row} /></TableCell>
                      <TableCell>
                        <Badge variant={row.status === "PENDING" ? "secondary" : row.status === "APPROVED" ? "default" : "destructive"}>
                          {t(`admin.accessRequests.status.${row.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {row.status !== "APPROVED" && (
                          <Button size="sm" disabled={!canApprove} onClick={() => setApproving(row)} className="mr-2">
                            {row.status === "REJECTED" ? t("admin.accessRequests.approveRejected") : t("admin.accessRequests.approve")}
                          </Button>
                        )}
                        {row.status === "PENDING" && (
                          <Button size="sm" variant="outline" disabled={busy === row.id} onClick={() => act(row, "reject")}>
                            {t("admin.accessRequests.reject")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={colCount} className="p-4">
                          <RequestDetail row={row} busy={busy === row.id} onAct={(a) => act(row, a)} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          <ChevronLeft className="w-4 h-4" /> {t("common.previous")}
        </Button>
        <div className="text-sm text-muted-foreground">
          {t("common.page")} {page} / {pages}
        </div>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
          {t("common.next")} <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <ApproveRequestDialog
        request={approving}
        onOpenChange={(open) => !open && setApproving(null)}
        onApproved={async (outcome) => {
          toast.success(t("admin.accessRequests.toastDone", { status: t(`admin.accessRequests.emailStatus.${outcome}`) }));
          await fetchRows();
          onChange?.();
        }}
      />
    </div>
  );
}

const ERROR_CODES = new Set([
  "already_decided", "registry_not_affiliated", "registry_unavailable", "role_conflict", "provider_email_taken", "invalid_email",
]);

/** Green only when the code-keyed affiliation AND the name agree; amber on a name doubt; red / grey block approval. */
function RegistryCheckBadge({ row }: { row: AccessRequestRow }) {
  const { t } = useTranslation();
  if (row.registryOutcome === "VERIFIED") {
    if (row.registryNameMatch === true) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
          <CheckCircle2 className="w-4 h-4" /> {t("admin.accessRequests.check.verified")}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
        <AlertTriangle className="w-4 h-4" />
        {row.registryNameMatch === false ? t("admin.accessRequests.check.nameMismatch") : t("admin.accessRequests.check.nameMissing")}
      </span>
    );
  }
  if (row.registryOutcome === "NOT_AFFILIATED") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
        <XCircle className="w-4 h-4" /> {t("admin.accessRequests.check.notAffiliated")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
      <CloudOff className="w-4 h-4" /> {t("admin.accessRequests.check.unavailable")}
    </span>
  );
}

function RequestDetail({ row, busy, onAct }: { row: AccessRequestRow; busy: boolean; onAct: (a: "recheck" | "resend-email") => void }) {
  const { t } = useTranslation();
  const s = row.registrySnapshot;
  const smartIdName = [row.givenName, row.familyName].filter(Boolean).join(" ") || "—";
  const fmt = (v: string | null | undefined) => (v ? new Date(v).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—");

  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-[minmax(120px,40%)_1fr] gap-x-3 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground break-words">{children ?? "—"}</span>
    </div>
  );
  const Section = ({ title, children, footer }: { title: string; children: React.ReactNode; footer?: React.ReactNode }) => (
    <div className="rounded-xl border border-border bg-card p-4 flex flex-col">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{title}</div>
      <div className="flex-1">{children}</div>
      {footer && <div className="mt-3 pt-3 border-t border-border">{footer}</div>}
    </div>
  );
  const emailStatus = (status: AccessRequestEmailStatus | null, at: string | null) =>
    status ? (
      <>
        <span className={cn(["REJECTED", "SUPPRESSED", "SKIPPED", "PENDING"].includes(status) && "text-amber-700 dark:text-amber-400")}>
          {t(`admin.accessRequests.emailStatus.${status}`)}
        </span>
        {at && <span className="text-muted-foreground"> · {fmt(at)}</span>}
      </>
    ) : (
      "—"
    );
  const nameVerdict =
    row.registryNameMatch === true
      ? <span className="text-green-700 dark:text-green-400">{t("admin.accessRequests.detail.namesMatch")}</span>
      : row.registryNameMatch === false
      ? <span className="text-amber-700 dark:text-amber-400">{t("admin.accessRequests.check.nameMismatch")}</span>
      : <span className="text-muted-foreground">{t("admin.accessRequests.check.nameMissing")}</span>;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Section title={t("admin.accessRequests.sections.applicant")}>
        <Field label={t("admin.accessRequests.detail.smartIdPerson")}>{smartIdName}</Field>
        <Field label={t("admin.accessRequests.detail.registryPerson")}>
          {s?.person?.name ?? "—"}
          {s?.person?.name && <span className="text-xs"> · {nameVerdict}</span>}
        </Field>
        <Field label={t("admin.accessRequests.colCode")}><span className="font-mono">{row.personalCode}</span></Field>
        <Field label={t("admin.accessRequests.detail.replyEmail")}>
          {row.email}
          <span className="text-muted-foreground text-xs"> · {t(`admin.accessRequests.emailSource.${row.emailSource}`)}</span>
        </Field>
        <div className="mt-3">
          <div className="text-xs text-muted-foreground mb-1">{t("admin.accessRequests.detail.comment")}</div>
          {row.comment ? (
            <div className="text-sm whitespace-pre-wrap border-l-2 border-border pl-3 text-foreground">{row.comment}</div>
          ) : (
            <div className="text-sm text-muted-foreground">{t("admin.accessRequests.detail.noComment")}</div>
          )}
        </div>
      </Section>

      <Section
        title={t("admin.accessRequests.sections.company")}
        footer={
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{t("admin.accessRequests.detail.checkedAt")}: {fmt(row.registryCheckedAt)}</span>
            {row.status !== "APPROVED" && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onAct("recheck")}>
                {t("admin.accessRequests.recheck")}
              </Button>
            )}
          </div>
        }
      >
        <Field label={t("admin.accessRequests.detail.company")}>
          {s?.company?.name ?? row.companyName}
          <span className="font-mono text-xs text-muted-foreground"> {row.registryCode}</span>
        </Field>
        <Field label={t("admin.accessRequests.detail.role")}>
          {row.role === "BUILDING" ? t("admin.accessRequests.roleBuilding") : t("admin.accessRequests.roleProvider")}
        </Field>
        <Field label={t("admin.accessRequests.detail.address")}>{s?.company?.address}</Field>
        <Field label={t("admin.accessRequests.detail.registryEmail")}>{s?.company?.email}</Field>
        <Field label={t("admin.accessRequests.detail.phone")}>{s?.company?.phone}</Field>
        <Field label={t("admin.accessRequests.detail.foundingDate")}>{s?.company?.foundingDate}</Field>
        <Field label={t("admin.accessRequests.detail.inSystemLabel")}>
          {s?.inSystem?.buildingId
            ? t("admin.accessRequests.detail.inSystemBuilding")
            : s?.inSystem?.providerId
            ? t("admin.accessRequests.detail.inSystemProvider")
            : t("admin.accessRequests.detail.notInSystem")}
        </Field>
        <div className="mt-3">
          <div className="text-xs text-muted-foreground mb-1">{t("admin.accessRequests.detail.affiliations")}</div>
          {s?.affiliations?.length ? (
            <ul className="text-sm space-y-0.5">
              {s.affiliations.map((a) => (
                <li key={a.registryCode} className={cn(a.registryCode === row.registryCode && "font-medium")}>
                  {a.name} <span className="font-mono text-xs text-muted-foreground">{a.registryCode}</span>
                  {a.role && <span className="text-xs text-muted-foreground"> · {a.role}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-muted-foreground">{t("admin.accessRequests.detail.noAffiliations")}</div>
          )}
        </div>
      </Section>

      <Section
        title={t("admin.accessRequests.sections.processing")}
        footer={
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onAct("resend-email")}>
            {t("admin.accessRequests.resendEmail")}
          </Button>
        }
      >
        <Field label={t("admin.accessRequests.detail.receivedLetter")}>{emailStatus(row.receivedEmailStatus, row.receivedEmailAt)}</Field>
        <Field label={t("admin.accessRequests.detail.decisionLetter")}>{emailStatus(row.decisionEmailStatus, row.decisionEmailAt)}</Field>
        {row.decidedAt && (
          <>
            <Field label={t("admin.accessRequests.detail.decision")}>
              {t(`admin.accessRequests.status.${row.status}`)}
              <span className="text-muted-foreground">
                {" · "}
                {row.decidedBySalesMember
                  ? t("admin.accessRequests.detail.curator", { name: row.decidedBySalesMember.name })
                  : row.decidedBy?.name ?? row.decidedById ?? "—"}
                {" · "}
                {fmt(row.decidedAt)}
              </span>
            </Field>
            <Field label={t("admin.accessRequests.detail.linkedEntity")}>{row.building?.name ?? row.provider?.name ?? "—"}</Field>
            {row.decisionEmail && <Field label={t("admin.accessRequests.detail.overrideEmail")}>{row.decisionEmail}</Field>}
          </>
        )}
        <div className="mt-3">
          <div className="text-xs text-muted-foreground mb-1">{t("admin.accessRequests.detail.events")}</div>
          <ul className="text-xs space-y-1">
            {row.events.map((e) => (
              <li key={e.id} className="flex gap-2">
                <span className="text-muted-foreground whitespace-nowrap">{fmt(e.createdAt)}</span>
                <span>
                  {t(`admin.accessRequests.events.${e.action}`, e.action)}
                  {e.actorKind === "sales" && e.actorName
                    ? ` · ${t("admin.accessRequests.detail.curator", { name: e.actorName })}`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Section>
    </div>
  );
}
